'use client'

import React, { useState } from 'react'
import dynamic from 'next/dynamic'
import { Offer, ProductData } from '@/services/dataTypes'
import { getFinalDiscountTag } from '@/constants/hooks'
import { Eye } from 'lucide-react'

const OfferModal = dynamic(() => import('@/components/Theme-18/components/OfferModal'), { ssr: false })
const ProductDetailsModal = dynamic(() => import('./ProductDetailsModal'), { ssr: false })

interface Props {
    offer: Offer | ProductData
    merchantHref: string
    imageSrc: string
    merchantImg?: string | null
    domain: string
    type?: 'anchor' | 'button'
    buttonClass?: string
    modalType?: 'simple' | 'details'
    companyId?: string
}

const OfferDetailsToggle = ({
    offer,
    merchantHref,
    domain,
    type = 'anchor',
    buttonClass,
    imageSrc,
    merchantImg,
    modalType = 'simple',
    companyId = ''
}: Props) => {
    const [showModal, setShowModal] = useState(false)

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setShowModal(true)
    }

    const originalPrice = offer?.original_price ? parseFloat(offer.original_price) : 0
    const salePrice = offer?.sale_price ? parseFloat(offer.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
        : null
    const finalDiscountTag = getFinalDiscountTag(offer?.offer_title || offer?.offer_detail, discountPercent)

    // Safely extract merchant name — Offer has merchant object, ProductData may not
    const merchantName = (offer as any)?.merchant?.merchant_name ?? ''

    return (
        <>
            <button
                onClick={handleClick}
                role={type === 'anchor' ? 'presentation' : undefined}
                className={`inline-flex items-center gap-1 text-[11px] sm:text-xs text-[#8C5826] hover:text-[#5e3814] cursor-pointer hover:underline font-bold transition-colors ${buttonClass ?? ''}`}
            >
                <Eye size={11} className="sm:w-3 sm:h-3" />
                <span>View Details</span>
            </button>

            {showModal && modalType === 'details' && (
                <ProductDetailsModal
                    product={offer as Offer}
                    merchantName={merchantName}
                    merchantLogo={merchantImg ?? ''}
                    domain={domain}
                    onClose={() => setShowModal(false)}
                />
            )}
            {showModal && modalType === 'simple' && (
                <OfferModal
                    data={offer as Offer}
                    companyId={companyId}
                    onClose={() => setShowModal(false)}
                    domain={domain}
                    finalDiscountTag={finalDiscountTag}
                    merchantHref={merchantHref}
                    merchantImg={merchantImg}
                    productImg={imageSrc}
                />
            )}
        </>
    )
}

export default OfferDetailsToggle
